import {
    faAward,
    faClock, faGears,
    faHandPeace,
    faHouse, faJetFighter,
    faMapLocationDot,
    faPaperPlane,
    faPlaneDeparture,
    faRankingStar, faReceipt, faStar, faFileExport
} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import "./profile.scss"
import {Link} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useEffect, useState} from "react";
import i18n from "i18next";
import api from "../../api/api";

export function Profile(){
    const { t } = useTranslation();
    const [pilot, setPilot] = useState(null);
    const [flights, setFlights] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [allowAcars, setAllowAcars] = useState(false);
    const [airlineCount, setAirlineCount] = useState(0);
    const [simbriefAccount, setSimbriefAccount] = useState('');
    const [editingSimbrief, setEditingSimbrief] = useState(false);
    const [savingSimbrief, setSavingSimbrief] = useState(false);

    useEffect(() => {
        const fetchPilotData = async () => {
            try {
                // Fetch ALLOW_ACARS configuration
                try {
                    const acarsResponse = await api.get('/configs/ALLOW_ACARS');
                    setAllowAcars(acarsResponse.data?.value === true || acarsResponse.data?.value === 'true');
                } catch (configError) {
                    console.error('Failed to fetch ALLOW_ACARS config:', configError);
                    setAllowAcars(false);
                }

                // Fetch airlines count to determine if change airline should be shown
                try {
                    const airlinesResponse = await api.get('/airlines');
                    setAirlineCount(airlinesResponse.data?.length || 0);
                } catch (airlinesError) {
                    console.error('Failed to fetch airlines:', airlinesError);
                    setAirlineCount(0);
                }

                const response = await api.get('/auth/me', {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                });
                if (response?.data?.pilot) {
                    const pilotData = response.data.pilot;
                    const currentLang = i18n.language.toUpperCase();

                    let filteredMedals = pilotData.medals || [];
                    if (filteredMedals.length > 0 && filteredMedals[0].lang) {
                        filteredMedals = filteredMedals.filter(
                            (medal) => medal.lang?.toUpperCase() === currentLang
                        );
                        if (filteredMedals.length === 0) {
                            filteredMedals = pilotData.medals.filter(
                                (medal) => medal.lang?.toUpperCase() === 'EN'
                            );
                        }
                        if (filteredMedals.length === 0) {
                            const availableLangs = [
                                ...new Set(pilotData.medals.map((medal) => medal.lang?.toUpperCase())),
                            ].filter(Boolean);
                            if (availableLangs.length > 0) {
                                filteredMedals = pilotData.medals.filter(
                                    (medal) => medal.lang?.toUpperCase() === availableLangs[0]
                                );
                            }
                        }
                    }

                    setPilot({ ...pilotData, medals: filteredMedals });
                    setSimbriefAccount(pilotData.simbriefAccount || '');

                    const flightsResponse = await api.get('/flights/my-flights', {
                        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                    });
                    setFlights(flightsResponse.data);

                    setError(null);
                } else {
                    throw new Error(t('failed-to-fetch-pilot'));
                }
            } catch (error) {
                console.error(t('failed-to-fetch-pilot'), {
                    message: error.message,
                    response: error.response?.data,
                    status: error.response?.status,
                    headers: error.response?.headers,
                });
                const errorMessage =
                    error.response?.data?.error || error.message || t('failed-to-fetch-pilot');
                setError(errorMessage);
            } finally {
                setLoading(false);
            }
        };

        fetchPilotData();
    }, [t, i18n.language]);

    const totalFlights = flights.filter(flight => flight.status === 2).length;
    const totalHours = flights
        .filter(flight => flight.status === 2) // Only accepted flights
        .reduce((acc, flight) => {
            if (flight.startFlight && flight.closeFlight) {
                const start = new Date(flight.startFlight);
                const end = new Date(flight.closeFlight);
                const diffMs = end - start;
                const hours = diffMs / (1000 * 60 * 60);
                return acc + hours;
            }
            return acc;
        }, 0);

    const formatHours = (hours) => {
        const totalMinutes = Math.round(hours * 60);
        const h = Math.floor(totalMinutes / 60);
        const m = totalMinutes % 60;
        return `${h}h${m.toString().padStart(2, '0')}m`;
    };

    const mostUsedAircraft = flights
        .filter(flight => flight.status === 2) // Only accepted flights
        .length > 0
        ? Object.entries(
        flights
            .filter(flight => flight.status === 2)
            .reduce((acc, flight) => {
                acc[flight.aircraft] = (acc[flight.aircraft] || 0) + 1;
                return acc;
            }, {})
    ).reduce((a, b) => (a[1] > b[1] ? a : b), [null, 0])[0] || 'N/A'
        : 'N/A';

    const getFlightStatus = (flight) => {
        if (flight.status === 2) return t('flight-accepted');
        if (flight.status === 3) return t('flight-rejected');
        if (flight.status === 1) {
            if (!flight.startFlight && !flight.closeFlight) {
                return t('flight-pending-departure');
            }
            if (flight.startFlight && !flight.closeFlight) {
                return t('flight-in-air');
            }
            if (flight.startFlight && flight.closeFlight) {
                return t('flight-pending-validation');
            }
        }
        return t('unknown');
    };

    const getFlightTime = (flight) => {
        if (flight.startFlight && flight.closeFlight) {
            const start = new Date(flight.startFlight);
            const end = new Date(flight.closeFlight);
            const diffMs = end - start;
            const hours = Math.floor(diffMs / (1000 * 60 * 60));
            const minutes = Math.round((diffMs % (1000 * 60 * 60)) / (1000 * 60));
            return `${hours}h${minutes.toString().padStart(2, '0')}m`;
        }
        return 'N/A';
    };

    const handleSaveSimbrief = async () => {
        setSavingSimbrief(true);
        try {
            const response = await api.put(`/pilots/${pilot.id}`, {
                simbriefAccount: simbriefAccount
            }, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            
            if (response.data) {
                setPilot({ ...pilot, simbriefAccount: simbriefAccount });
                setEditingSimbrief(false);
                alert(t('SimBrief account updated successfully'));
            }
        } catch (error) {
            console.error('Failed to update SimBrief account:', error);
            alert(t('Failed to update SimBrief account'));
        } finally {
            setSavingSimbrief(false);
        }
    };

    if (loading) return <div>{t('loading')}</div>;
    if (error) return <p className="error-message">{error}</p>;
    if (!pilot) return <div>{t('no-pilot-data')}</div>;

    return (<>
            <div className="profile">
                <h1>{t('profile-greeting')} <span>{pilot.firstName}</span> <FontAwesomeIcon icon={faHandPeace}/></h1>
                <div className="profile-content">
                    <div className="container-profile">
                        <div className="text-wrapper">
                            <h2>{pilot.locationIcao || 'N/A'}</h2>
                            <p>{t('profile-location')}</p>
                        </div>
                        <div className="icon-text">
                            <FontAwesomeIcon icon={faMapLocationDot}/>
                        </div>
                    </div>
                    <div className="container-profile">
                        <div className="text-wrapper">
                            <h2>{pilot.airline?.name || 'N/A'}</h2>
                            <p>{t('profile-airline')}</p>
                        </div>
                        <div className="icon-text">
                            <FontAwesomeIcon icon={faPaperPlane}/>
                        </div>
                    </div>
                    <div className="container-profile">
                        <div className="text-wrapper">
                            <h2>{pilot.rank?.name || 'N/A'}</h2>
                            <p>{t('profile-rank')}</p>
                        </div>
                        <div className="icon-text">
                            <FontAwesomeIcon icon={faRankingStar}/>
                        </div>
                    </div>
                    <div className="container-profile">
                        <div className="text-wrapper">
                            <h2>{pilot.hub?.airport.icao || 'N/A'}</h2>
                            <p>{t('profile-hub')}</p>
                        </div>
                        <div className="icon-text">
                            <FontAwesomeIcon icon={faHouse}/>
                        </div>
                    </div>
                    <div className="container-profile">
                        <div className="text-wrapper">
                            <h2>{totalFlights}</h2>
                            <p>{t('profile-flights')}</p>
                        </div>
                        <div className="icon-text">
                            <FontAwesomeIcon icon={faPlaneDeparture}/>
                        </div>
                    </div>
                    <div className="container-profile">
                        <div className="text-wrapper">
                            <h2>{formatHours(totalHours)}</h2>
                            <p>{t('profile-hours')}</p>
                        </div>
                        <div className="icon-text">
                            <FontAwesomeIcon icon={faClock}/>
                        </div>
                    </div>
                    <div className="container-profile">
                        <div className="text-wrapper">
                            <h2>{pilot.points || 0}</h2>
                            <p>{t('profile-points')}</p>
                        </div>
                        <div className="icon-text">
                            <FontAwesomeIcon icon={faStar}/>
                        </div>
                    </div>
                    <div className="container-profile">
                        <div className="text-wrapper">
                            <h2>{mostUsedAircraft}</h2>
                            <p>{t('profile-most-used-aircraft')}</p>
                        </div>
                        <div className="icon-text">
                            <FontAwesomeIcon icon={faJetFighter}/>
                        </div>
                    </div>
                </div>
                <div className={"profile-module"}>
                    <h3><FontAwesomeIcon icon={faGears} />  {t('profile-options')}</h3>
                    <div className={"button-list"}>
                        <Link to="/change-position" className="btn">
                            {t('profile-change-position')}
                        </Link>
                        <Link to="/change-hub" className="btn">
                            {t('profile-change-hub')}
                        </Link>
                        {airlineCount > 1 && (
                            <Link to="/change-airline" className="btn">
                                {t('profile-change-airline')}
                            </Link>
                        )}
                        {allowAcars && (
                            <Link to="/acars" className="btn">
                                {t('profile-acars')}
                            </Link>
                        )}
                        <Link to="/delete-account" className="btn danger">
                            {t('profile-delete-account')}
                        </Link>
                    </div>
                </div>
                <div className={"profile-module"}>
                    <h3><FontAwesomeIcon icon={faPlaneDeparture}/> SimBrief Integration</h3>
                    <div style={{ padding: '15px' }}>
                        {!editingSimbrief ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span>SimBrief Account: </span>
                                <strong>{simbriefAccount || 'Not configured'}</strong>
                                <button 
                                    className="btn" 
                                    style={{ marginLeft: 'auto' }}
                                    onClick={() => setEditingSimbrief(true)}
                                >
                                    {simbriefAccount ? 'Edit' : 'Configure'}
                                </button>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <label>SimBrief Username:</label>
                                <input 
                                    type="text"
                                    value={simbriefAccount}
                                    onChange={(e) => setSimbriefAccount(e.target.value)}
                                    placeholder="Enter your SimBrief username"
                                    style={{ 
                                        padding: '8px', 
                                        borderRadius: '5px',
                                        border: '1px solid var(--background-secondary)',
                                        backgroundColor: 'var(--background-primary)',
                                        color: 'var(--text-primary)'
                                    }}
                                />
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button 
                                        className="btn"
                                        onClick={handleSaveSimbrief}
                                        disabled={savingSimbrief}
                                    >
                                        {savingSimbrief ? 'Saving...' : 'Save'}
                                    </button>
                                    <button 
                                        className="btn secondary"
                                        onClick={() => {
                                            setEditingSimbrief(false);
                                            setSimbriefAccount(pilot.simbriefAccount || '');
                                        }}
                                        disabled={savingSimbrief}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className={"profile-module"}>
                    <h3><FontAwesomeIcon icon={faAward}/> {t('profile-awards')}</h3>
                    {pilot.medals.length > 0 ? (
                        <div className="awards-list">
                            {pilot.medals.map((medal) => (
                                <div key={medal.id} className="award-item">
                                    <img src={medal.img} alt={medal.text} />
                                    <p>{medal.text}</p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p>{t('no-awards')}</p>
                    )}
                </div>
                <div className={"profile-module"}>
                    <h3><FontAwesomeIcon icon={faReceipt}/> {t('profile-logbook')}</h3>
                    <div className="table-container">
                        <table className="pilot-table">
                            <thead>
                            <tr>
                                <th>{t('profile-logbook-callsign')}</th>
                                <th>{t('profile-logbook-aircraft')}</th>
                                <th>{t('profile-logbook-departure')}</th>
                                <th>{t('profile-logbook-arrival')}</th>
                                <th>{t('profile-logbook-start')}</th>
                                <th>{t('profile-logbook-end')}</th>
                                <th>{t('profile-logbook-network')}</th>
                                <th>{t('status')}</th>
                                <th>{t('profile-logbook-time')}</th>
                            </tr>
                            </thead>
                            <tbody>
                            {flights.length > 0 ? (
                                flights.map((flight, index) => (
                                    <tr key={flight.id} className={"background-change"}>
                                        <td>{flight.callsign || 'N/A'}</td>
                                        <td>{flight.aircraft}</td>
                                        <td>{flight.departure.icao}</td>
                                        <td>{flight.arrival.icao}</td>
                                        <td>
                                            {flight.startFlight
                                                ? new Date(flight.startFlight).toLocaleString()
                                                : 'N/A'}
                                        </td>
                                        <td>
                                            {flight.closeFlight
                                                ? new Date(flight.closeFlight).toLocaleString()
                                                : 'N/A'}
                                        </td>
                                        <td>{flight.network}</td>
                                        <td>{getFlightStatus(flight)}</td>
                                        <td>{getFlightTime(flight)}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr className={"background-change"}>
                                    <td colSpan={9} style={{ textAlign: 'center' }}>
                                        {t('no-flights-found')}
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </>
    )
}